import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const TemplateForm = ({ template, onSave, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    template_type: template?.template_type || 'patient_visit',
    tasks: template?.tasks || []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(template?.id, formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { title: '', description: '', day_offset: 0, role: 'site_coordinator' }]
    }));
  };

  const removeTask = (index) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const updateTask = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) => 
        i === index ? { ...task, [field]: value } : task
      )
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="template-name">Template Name *</Label>
          <Input
            id="template-name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter template name"
            required
          />
        </div>
        <div>
          <Label htmlFor="template-type">Template Type</Label>
          <Select value={formData.template_type} onValueChange={(value) => handleChange('template_type', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="study_selection">Study Selection</SelectItem>
              <SelectItem value="study_startup">Study Startup</SelectItem>
              <SelectItem value="patient_visit">Patient Visit</SelectItem>
              <SelectItem value="monitoring_visit">Monitoring Visit</SelectItem>
              <SelectItem value="ae_sae">AE/SAE</SelectItem>
              <SelectItem value="protocol_ib_change">Protocol/IB Change</SelectItem>
              <SelectItem value="closeout_visit">Closeout Visit</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Template Tasks</Label>
          <Button type="button" variant="outline" size="sm" onClick={addTask}>
            <Plus className="h-4 w-4 mr-1" /> Add Task
          </Button>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {formData.tasks.map((task, index) => (
            <div key={`template-task-${index}-${task.title?.slice(0,15) || 'new'}`} className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-start gap-2">
                <div className="flex-1 grid grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <Input
                      value={task.title}
                      onChange={(e) => updateTask(index, 'title', e.target.value)}
                      placeholder="Task title"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={task.day_offset}
                      onChange={(e) => updateTask(index, 'day_offset', parseInt(e.target.value))}
                      placeholder="Day offset"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Select value={task.role} onValueChange={(value) => updateTask(index, 'role', value)}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="site_coordinator">Coordinator</SelectItem>
                        <SelectItem value="study_lead">Study Lead</SelectItem>
                        <SelectItem value="qc">QC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTask(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-2">
                <Textarea
                  value={task.description || ''}
                  onChange={(e) => updateTask(index, 'description', e.target.value)}
                  placeholder="Task description (optional)"
                  rows={2}
                  className="text-sm"
                />
              </div>
            </div>
          ))}
          {formData.tasks.length === 0 && (
            <p className="text-center text-gray-500 py-4">No tasks added. Click "Add Task" to begin.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {isEdit ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
};
